import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTrialStatus } from '@/services/subscription';

export function TrialBanner() {
  const { data: trial } = useTrialStatus();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (!trial?.isTrialing || dismissed) return null;

  const isUrgent = trial.daysRemaining <= 3;
  const isExpiringSoon = trial.daysRemaining <= 7;

  const timeLabel = trial.daysRemaining === 0
    ? `${trial.hoursRemaining}h remaining`
    : trial.daysRemaining === 1
      ? '1 day remaining'
      : `${trial.daysRemaining} days remaining`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className="relative flex items-center justify-between gap-3 rounded-2xl px-5 py-3 text-sm"
        style={{
          background: isUrgent
            ? 'linear-gradient(135deg, rgba(244,63,94,0.12) 0%, rgba(239,68,68,0.06) 100%)'
            : 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(79,70,229,0.06) 100%)',
          border: `1px solid ${isUrgent ? 'rgba(244,63,94,0.25)' : 'rgba(99,102,241,0.20)'}`,
        }}
      >
        {/* Left: info */}
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex h-7 w-7 flex-none items-center justify-center rounded-lg"
            style={{
              background: isUrgent ? 'rgba(244,63,94,0.15)' : 'rgba(99,102,241,0.15)',
            }}
          >
            {isUrgent
              ? <Clock className="h-3.5 w-3.5 text-rose-400" />
              : <Sparkles className="h-3.5 w-3.5 text-accent-400" />
            }
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-semibold text-white">
              {isUrgent ? '⚡ Trial ending soon' : 'Professional Trial'}
            </span>
            <span
              className="font-mono text-[11px] rounded-full px-2 py-0.5"
              style={{
                background: isUrgent ? 'rgba(244,63,94,0.20)' : 'rgba(99,102,241,0.15)',
                color: isUrgent ? '#fb7185' : '#a5b4fc',
              }}
            >
              {timeLabel}
            </span>
            <span className="hidden text-white/50 sm:inline">
              {isExpiringSoon
                ? 'Add a payment method to keep your features.'
                : 'You have full Professional access. Upgrade to keep it.'}
            </span>
          </div>
        </div>

        {/* Right: CTA + dismiss */}
        <div className="flex flex-none items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/app/billing')}
            className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition-all"
            style={{
              background: isUrgent ? 'rgba(244,63,94,0.25)' : 'rgba(99,102,241,0.25)',
            }}
          >
            Upgrade now
            <ArrowRight className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="flex h-6 w-6 items-center justify-center rounded-lg text-white/30 transition-colors hover:text-white/60"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
